import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { UUID } from '@/types/Id'
import { NodeType, Permission } from '@/types/Permission'

import DataLoader from '@/vendor/dataloader'

import {
  PermissionBulkCheckRequest,
  PermissionBulkCheckResponse,
} from './types'
import { PermissionKey } from './types'

type AccessTokenGetter = () => Promise<string | undefined>

const api = {
  checkBulkPermissions: async (
    resourceType: NodeType,
    resourceIds: UUID[],
    permissions: Permission[],
    accessToken: string,
  ): Promise<PermissionBulkCheckResponse> => {
    const payload: PermissionBulkCheckRequest = {
      resource_ids: resourceIds,
      permissions,
    }

    const res = await fetch(
      `${API_URL}/api/v1/permissions-bulk/${resourceType}/check`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      },
    )

    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }

    return (await res.json()) as PermissionBulkCheckResponse
  },
}

const batchPermissions = async (
  keys: readonly PermissionKey[],
  getAccessToken: AccessTokenGetter,
): Promise<(boolean | Error)[]> => {
  const accessToken = await getAccessToken()

  if (!accessToken) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Missing access token',
    })
  }

  const requestsByType = new Map<
    NodeType,
    { resourceIds: UUID[]; permissions: Set<Permission> }
  >()
  for (const key of keys) {
    if (!requestsByType.has(key.resourceType)) {
      requestsByType.set(key.resourceType, {
        resourceIds: [],
        permissions: new Set(),
      })
    }
    const group = requestsByType.get(key.resourceType)!
    group.resourceIds.push(key.resourceId)
    group.permissions.add(key.permission)
  }

  const resourceTypes = Array.from(requestsByType.keys())
  const promises = resourceTypes.map((resourceType) => {
    const { resourceIds, permissions } = requestsByType.get(resourceType)!
    return api
      .checkBulkPermissions(
        resourceType,
        resourceIds,
        Array.from(permissions),
        accessToken,
      )
      .then((response) => ({
        resourceIdsCount: resourceIds.length,
        resourceType,
        response,
      }))
  })

  const results = await Promise.all(promises)
  const resultMap = new Map<string, boolean>()

  results.forEach(({ resourceType, response }) => {
    for (const [id, perms] of Object.entries(response.permissions)) {
      for (const [perm, hasAccess] of Object.entries(perms)) {
        const uniqueKey = `${resourceType}::${id}::${perm}`
        resultMap.set(uniqueKey, hasAccess)
      }
    }
  })

  return keys.map((key) => {
    const uniqueKey = `${key.resourceType}::${key.resourceId}::${key.permission}`
    const result = resultMap.get(uniqueKey)
    return result === undefined ? false : result
  })
}

export const createPermissionLoader = (getAccessToken: AccessTokenGetter) => {
  return new DataLoader<PermissionKey, boolean, string>(
    (keys) => batchPermissions(keys, getAccessToken),
    {
      //batchScheduleFn: (fn) => setTimeout(fn, 100),
      cacheKeyFn: (key) =>
        `${key.resourceType}::${key.resourceId}::${key.permission}`,
    },
  )
}
